import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  type NormalizedCacheObject,
  from,
} from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';

import { type ExtendedFetcher } from './atlassian-client-types';

const jiraTypes = [
  'JiraIssueFieldConfiguration',
  'JiraProjectField',
  'JiraIssueTypeField',
  'JiraSingleLineTextField',
  'JiraRichTextField',
  'JiraPriorityField',
  'JiraSprintField',
  'JiraLabelsField',
  'JiraSingleSelectUserPickerField',
  'JiraMultipleSelectUserPickerField',
  'JiraEpicLinkField',
  'JiraDatePickerField',
  'JiraDateTimePickerField',
  'JiraNumberField',
  'JiraComponentsField',
  'JiraSingleSelectField',
  'JiraAttachmentsField',
  'JiraIssueLinkField',
  'JiraCheckboxesField',
  'JiraCascadingSelectField',
  'JiraConnectMultipleSelectField',
  'JiraConnectNumberField',
  'JiraConnectReadOnlyField',
  'JiraConnectRichTextField',
  'JiraConnectSingleSelectField',
  'JiraConnectTextField',
  'JiraDevSummaryField',
  'JiraFlagField',
  'JiraStatusCategoryField',
  'JiraIssueRestrictionField',
  'JiraBooleanField',
  'JiraColorField',
  'JiraMultipleGroupPickerField',
  'JiraMultipleSelectField',
  'JiraMultipleVersionPickerField',
  'JiraOriginalTimeEstimateField',
  'JiraParentIssueField',
  'JiraPeopleField',
  'JiraRadioSelectField',
  'JiraRequestTypeField',
  'JiraResolutionField',
  'JiraSecurityLevelField',
  'JiraSingleGroupPickerField',
  'JiraSingleVersionPickerField',
  'JiraStatusField',
  'JiraSubtasksField',
  'JiraTeamField',
  'JiraTeamViewField',
  'JiraTimeTrackingField',
  'JiraUrlField',
  'JiraVotesField',
  'JiraWatchesField',
  'JiraWorkCategoryField',
  'JiraWorkLogField',
  'JsmRequestTypeField', // Deprecated in favour of JiraServiceManagementRequestTypeField
  'JsmMultipleSelectUserPickerField',
  'JsmOrganizationField',
  'JsmPeopleField',
  'JiraServiceManagementRequestTypeField',
  'JiraAtlassianTeamField',
];

/**
 * Apollo isn't designed to be used with multiple endpoints.
 * To utilise caching here we store multiple clients by baseurl
 * getting them when a query is requested.
 *
 * Consider memory when using this class. It's intended use is to only
 * have a few ApolloClients in memory at one time.
 */
export class DelegatingApolloClient {
  constructor(private readonly fetcher?: ExtendedFetcher) {}

  private readonly apolloClients: Map<string, ApolloClient<NormalizedCacheObject>> =
    new Map();

  getOrCreate(baseUrl: string, endpoint: string) {
    let client = this.apolloClients.get(baseUrl);

    if (!client) {
      const extendedFetcher: ExtendedFetcher | undefined = this.fetcher;
      const linkFetch: typeof fetch = (input: RequestInfo | URL, init?: RequestInit) => {
        const normalisedInput: RequestInfo =
          input instanceof URL ? input.toString() : input;
        return extendedFetcher
          ? extendedFetcher(normalisedInput, init)
          : fetch(input, init);
      };

      client = new ApolloClient({
        cache: new InMemoryCache({
          possibleTypes: {
            JiraIssueField: jiraTypes,
            JiraIssueFieldConfiguration: jiraTypes,
          },
        }),
        defaultOptions: {},
        link: from([
          // Only leverage Apollo's RetryLink when no custom fetcher is provided.
          ...(!this.fetcher
            ? [
                new RetryLink({
                  delay: { initial: 200, max: 1000, jitter: true },
                  attempts: { max: 3 },
                }),
              ]
            : []),
          createHttpLink({
            uri: `${baseUrl}${endpoint}`,
            // Leverage & override fetch in `createHttpLink` (typically with retries) when provided
            ...(this.fetcher ? { fetch: linkFetch } : {}),
          }),
        ]),
      });

      this.apolloClients.set(baseUrl, client);
    }

    return client;
  }
}
